import { Alert, Box, Button, Container, Header, SpaceBetween } from '@amzn/awsui-components-core';
import {
  Content,
  ContentAudience,
  ContentType,
  ContentWorkflow,
  ReviewDecision,
  ReviewRequestDecision,
} from '@amzn/awscat-pattern-library-service-model';
import React, { useEffect, useState, type JSX } from 'react';
import constants from '../../../common/constants';
import {
  apgCodeGuidelinesLink,
  authorBrReviewGuideLink,
  awsWellArchitectPrinciplesLink,
  contentChecklistListName,
  contentSuggestionsLink,
  hasCodeLinks,
  isForInternalContentAudience,
  latestIconsLink,
  notAllowedToDeleteReviewerAlert,
} from './ContentUtils';
import SubmitForReviewControl from './SubmitForReviewControl';
import ChecklistGroup from '../../common/ChecklistGroup';
import ChecklistInfo from '../../../model/ChecklistInfo';

interface Props {
  handleAddReviewer: () => void;
  workflow: ContentWorkflow;
  isContentUnchanged: boolean;
  handleSaveChanges: (unlock: boolean) => Promise<any>;
  handleSubmitForReview: () => Promise<any>;
  handleChecklistComplete: () => void;
  handleChecklistIncomplete: () => void;
  isReviewersValid: boolean;
  validateSubmitForReview: () => Promise<boolean>;
  submitForReviewDisabled: boolean;
  updatedContent: Content;
  updatedContentAudience: ContentAudience;
  shouldResetChecklist: boolean;
  handleResetChecklist: () => void;
}

const legalItems: string[] = [
  "Customer's company name and all references to customer",
  'Customer personnel names and contact information',
  'Any human names, dates of birth, social security numbers, email addresses, or any other personal information',
  'All customer logos and marks',
  "Customer's or partner's intellectual property",
  'Any information marked as "confidential information" by the customer or partner',
  'Any other proprietary customer or partner information',
];

const ContentChecklist: React.FunctionComponent<Props> = (props: Props) => {
  const {
    isContentUnchanged,
    handleSaveChanges,
    handleSubmitForReview,
    workflow,
    handleAddReviewer,
    handleChecklistComplete,
    handleChecklistIncomplete,
    isReviewersValid,
    submitForReviewDisabled,
    validateSubmitForReview,
    updatedContent,
    updatedContentAudience,
    shouldResetChecklist,
    handleResetChecklist,
  } = props;
  const { contentId, draftWorkflowId, lastReviewDecision, lastReviewRequestDecision } = workflow;
  const [allChecked, setAllChecked] = useState<boolean>(false);

  const { contentMetadata, contentTypeMetadata } = updatedContent;
  const { contentType } = contentMetadata;
  const hasCode = hasCodeLinks(contentTypeMetadata);
  const isForInternalAudience = isForInternalContentAudience(updatedContentAudience);

  useEffect(() => {
    if (allChecked) {
      handleChecklistComplete();
    } else {
      handleChecklistIncomplete();
    }
  }, [allChecked, handleChecklistComplete, handleChecklistIncomplete]);

  const strategyItems: Array<string | JSX.Element> = [
    'All links are accessible by the intended audience.',
    'Diagram source files (.pptx, .xlsx, .docx) are attached.',
    'The strategy describes the business use case (e.g., migration, modernization).',
    'The strategy identifies one or more customer business outcomes.',
    <>
      <span>
        The strategy is supported by guides and patterns in APG Library, or if applicable, I have
        submitted{' '}
      </span>
      {contentSuggestionsLink}
      <span>.</span>
    </>,
    'The strategy is original, not a duplicate or possibly plagiarized.',
    'The strategy covers the right level of depth and detail for senior management and C-level executives.',
    'The resources section includes links that complement the strategy.',
    'The resources section includes links to AWS and AWS Partner solutions (not competitor products).',
    'The strategy includes the latest AWS services and methodology guidance.',
    <>
      <span>The strategy adheres to the </span>
      {awsWellArchitectPrinciplesLink}
      <span>.</span>
    </>,
  ];

  const guideItems: Array<string | JSX.Element> = [
    hasCode ? (
      <>
        <span>All included code adheres to </span>
        {apgCodeGuidelinesLink}
        <span>.</span>
      </>
    ) : (
      ''
    ),
    'The guide is original, not a duplicate or possibly plagiarized.',
    'All links are accessible by the intended audience.',
    'Diagram source files (.pptx) are attached.',
    'The guide describes the business use case (e.g., migration, modernization).',
    'The guide identifies one or more customer business outcomes.',
    <>
      <span>
        The guide is supported by a strategy and patterns in APG Library, or if applicable, I have
        submitted{' '}
      </span>
      {contentSuggestionsLink}
      <span>.</span>
    </>,
    'The guide covers the right level of depth and detail for technical architects and leads.',
    'The guide covers multiple options, including pros and cons of each option.',
    'The information in the guide is correct and up to date.',
    <>
      <span>The guide adheres to the </span>
      {awsWellArchitectPrinciplesLink}
      <span>.</span>
    </>,
  ];

  const patternItems: Array<string | JSX.Element> = [
    hasCode ? (
      <>
        <span>All included code adheres to </span>
        {apgCodeGuidelinesLink}
        <span>.</span>
      </>
    ) : (
      ''
    ),
    'The pattern is original, not a duplicate or possibly plagiarized.',
    'All links are accessible by the intended audience.',
    'Diagram source files (.pptx) are attached.',
    <>
      <span>
        The pattern is supported by a guide in APG Library, or if applicable, I have submitted{' '}
      </span>
      {contentSuggestionsLink}
      <span>.</span>
    </>,
    'The pattern covers the right level of depth and detail for developers or other implementers.',
    <>
      <span>The pattern has an accurate architecture diagram that uses the </span>
      {latestIconsLink}
      <span>.</span>
    </>,
    'The prerequisites and limitations section provides complete information.',
    'The epics section provides the instructions required to complete the pattern.',
    isForInternalAudience
      ? ''
      : 'The pattern contains no internal tools, links, or proprietary information.',
    'The information in the pattern is correct and up to date.',
    <>
      <span>The pattern adheres to the </span>
      {awsWellArchitectPrinciplesLink}
      <span>.</span>
    </>,
  ];

  const contentTypeItemMap = new Map<ContentType, Array<string | JSX.Element>>([
    [ContentType.Strategy, strategyItems],
    [ContentType.Guide, guideItems],
    [ContentType.Pattern, patternItems],
  ]);

  const openContentItems = (contentTypeItemMap.get(contentType) || []).filter((item) => !!item);

  const contentChecklistInfo: ChecklistInfo = new ChecklistInfo(
    constants.OPEN_CONTENT_ITEMS_TEXT,
    openContentItems,
    '',
  );

  const legalItemsChecklistInfo: ChecklistInfo = new ChecklistInfo(
    constants.OPEN_LEGAL_ITEMS_TEXT,
    legalItems,
    constants.OPEN_LEGAL_ITEMS_INSTRUCTION,
  );

  const itemsChecklist = (
    <>
      <span>Upon completion of your content, please review it and verify the following. </span>
      <span>
        Content will be evaluated for these items by a SME and might not be approved if it does not
        meet the following criteria.{' '}
      </span>
      <span>For more details </span>
      {authorBrReviewGuideLink}
      <span>.</span>
      <ChecklistGroup
        contentId={contentId}
        listName={contentChecklistListName}
        onAllChecked={setAllChecked}
        checklistInfo={[contentChecklistInfo, legalItemsChecklistInfo]}
        workflowId={draftWorkflowId}
        shouldResetChecklist={shouldResetChecklist}
        handleResetChecklist={handleResetChecklist}
      />
    </>
  );

  const addReviewerDisabled =
    !allChecked ||
    (lastReviewDecision === ReviewDecision.DISAPPROVE &&
      lastReviewRequestDecision !== ReviewRequestDecision.DECLINE);
  const addReviewerButton = (
    <Box textAlign="left">
      <Button disabled={addReviewerDisabled} onClick={handleAddReviewer} fullWidth>
        {constants.ADD_REVIEWER}
      </Button>
    </Box>
  );

  // To submit for review, content should be:
  // 1. valid (i.e. no required fields missing), and
  // 2. content is new or modified or changed (but not saved), and
  // 3. has selected reviewers,
  // 4. all checklist items are checked
  const submitDisabled = submitForReviewDisabled || !allChecked;

  const reviewerControls = (
    <SubmitForReviewControl
      isContentUnchanged={isContentUnchanged}
      onSaveChanges={handleSaveChanges}
      onSubmitForReview={handleSubmitForReview}
      validateSubmitForReview={validateSubmitForReview}
      fullwidth
      disabled={submitDisabled}
      workflow={props.workflow}
    />
  );

  const checklistIncompleteAlert = allChecked ? null : (
    <Alert type={'warning'}>{constants.CONTENT_CHECKLIST_INCOMPLETE_ALERT}</Alert>
  );

  const cannotDeleteReviewerAlert =
    allChecked &&
    lastReviewDecision === ReviewDecision.DISAPPROVE &&
    lastReviewRequestDecision !== ReviewRequestDecision.DECLINE
      ? notAllowedToDeleteReviewerAlert
      : null;

  const noReviewerAlert =
    !allChecked || isReviewersValid ? null : (
      <Alert type={'warning'}>{constants.PEER_REVIEWERS_MISSING}</Alert>
    );

  const buttons = (
    <Box margin={{ top: 'xl' }}>
      <SpaceBetween id="checklist-buttons" direction={'vertical'} size={'xxs'}>
        {addReviewerButton}
        {reviewerControls}
        {checklistIncompleteAlert}
        {noReviewerAlert}
        {cannotDeleteReviewerAlert}
      </SpaceBetween>
    </Box>
  );

  return (
    <Box padding={'s'}>
      <Container header={<Header variant="h3">{constants.CONTENT_CHECKLIST_HEADER}</Header>}>
        {itemsChecklist}
        {buttons}
      </Container>
    </Box>
  );
};

export default ContentChecklist;
